/**
 * Orçamentos — proposta para família que ainda não veio à escola.
 *
 * Existe porque a equipe estava criando visita com data no passado só para
 * conseguir gerar o PDF. Isso enchia a agenda de encontros que nunca houve,
 * derrubava a taxa de conversão e ainda arriscava disparar para a família um
 * WhatsApp confirmando uma visita inexistente.
 *
 * O ciclo aqui é de três passos, na ordem em que a pessoa trabalha:
 *
 *   1. **Gerar PDF** — abre a proposta, salva como PDF pelo diálogo do navegador.
 *   2. **Abrir WhatsApp** — abre a conversa com o texto pronto, para anexar o arquivo.
 *   3. **Marcar como enviado** — carimba quem mandou e quando.
 *
 * O passo 3 é separado do 2 de propósito: o `wa.me` abre a conversa, mas não
 * tem como saber se a pessoa realmente anexou e enviou. Perguntar é honesto;
 * carimbar sozinho encheria a lista de "enviados" que ninguém enviou.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { patch, post } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useDadosApoio } from "../lib/dados";
import { buscarVisitas } from "../lib/visitas";
import { FormularioVisita, paraPayload, type EstadoFormulario } from "../components/FormularioVisita";
import { FichaImpressao } from "../components/FichaImpressao";
import { Cabecalho } from "../components/Layout";
import { cursoCurto, dataHoraCurta, moeda } from "../lib/formato";
import { telefoneExibicao } from "../lib/paises";
import { linkWhatsapp, mensagemOrcamento } from "../lib/mensagensRetomada";
import type { Visita } from "../lib/tipos";

const VERDE = "#0e8c3a";

/** Só o que a lista precisa saber sobre a proposta, para não repetir cálculo. */
function valorResumo(v: Visita): string {
  const num = (x: unknown) => {
    const n = Number(x ?? 0);
    return Number.isFinite(n) && n > 0 ? n : null;
  };
  const parcial = num(v.proposta?.valorComDesconto);
  const integral = num(v.proposta?.valorComDescontoIntegral);
  if (parcial === null && integral === null) return "—";
  if (integral === null) return moeda(parcial);
  if (parcial === null) return `${moeda(integral)} (integral)`;
  return `${moeda(parcial)} / ${moeda(integral)}`;
}

export default function Orcamentos() {
  const { podeAoMenos } = useAuth();
  const podeEditar = podeAoMenos("CONSULTOR");
  const apoio = useDadosApoio();

  const [itens, setItens] = useState<Visita[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [busca, setBusca] = useState("");
  const [somentePendentes, setSomentePendentes] = useState(false);

  const [editando, setEditando] = useState<Visita | null | undefined>(undefined);
  const [imprimindo, setImprimindo] = useState<Visita | null>(null);
  const [confirmando, setConfirmando] = useState<Visita | null>(null);
  const [promovendo, setPromovendo] = useState<Visita | null>(null);
  const [quando, setQuando] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState("");

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await buscarVisitas({ tipo: "ORCAMENTO", busca, porPagina: 500 });
      setItens(r.itens);
      setErro("");
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao carregar os orçamentos");
    } finally {
      setCarregando(false);
    }
  }, [busca]);

  useEffect(() => {
    const t = setTimeout(carregar, 300);
    return () => clearTimeout(t);
  }, [carregar]);

  const visiveis = useMemo(
    () => (somentePendentes ? itens.filter((v) => !v.orcamentoEnviadoEm) : itens),
    [itens, somentePendentes],
  );
  const pendentes = itens.filter((v) => !v.orcamentoEnviadoEm).length;

  async function salvar(f: EstadoFormulario) {
    const corpo = paraPayload(f);
    if (editando) await patch(`/api/visitas/${editando.id}`, corpo);
    else await post("/api/visitas", corpo);
    setEditando(undefined);
    await carregar();
  }

  /** Abre a conversa com o texto pronto; o anexo a pessoa põe na mão. */
  function abrirWhatsapp(v: Visita) {
    const url = linkWhatsapp(
      v.telefoneResponsavel,
      mensagemOrcamento(v.nomeResponsavel, v.nomeAluno, v.serie?.nome),
      v.paisTelefone,
    );
    if (!url) {
      setAviso(`${v.nomeResponsavel} está sem telefone válido — cadastre o número antes de enviar.`);
      return;
    }
    window.open(url, "_blank", "noopener");
    setConfirmando(v);
  }

  async function marcarEnviado(v: Visita) {
    setOcupado(true);
    try {
      await post(`/api/visitas/${v.id}/orcamento-enviado`);
      setConfirmando(null);
      await carregar();
    } catch (e) {
      setAviso(`Não foi possível registrar o envio: ${e instanceof Error ? e.message : "erro"}`);
    } finally {
      setOcupado(false);
    }
  }

  async function confirmarPromocao() {
    if (!promovendo || !quando) return;
    setOcupado(true);
    try {
      await post(`/api/visitas/${promovendo.id}/promover`, { inicioEm: new Date(quando).toISOString() });
      setPromovendo(null);
      setQuando("");
      await carregar();
      setAviso(`${promovendo.nomeAluno} virou visita agendada. A confirmação por WhatsApp foi disparada.`);
    } catch (e) {
      setAviso(`Não foi possível agendar: ${e instanceof Error ? e.message : "erro"}`);
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      <Cabecalho titulo="Orçamentos" />

      <div style={{ background: "#f0faf4", border: "1px solid #c6e9d4", borderRadius: 10, padding: "12px 16px", marginBottom: 18, fontSize: 13, color: "#1a5c2e", lineHeight: 1.6 }}>
        Proposta para família que ainda <strong>não visitou</strong> a escola. Não ocupa horário na
        agenda, não entra nos indicadores e não dispara WhatsApp automático — o envio do PDF é você
        quem faz, e o sistema registra.
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 18, alignItems: "center" }}>
        <input
          className="campo"
          style={{ flex: "1 1 220px", maxWidth: 320 }}
          placeholder="🔍 Pesquisar por aluno, responsável ou telefone"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--texto-suave)", cursor: "pointer" }}>
          <input type="checkbox" checked={somentePendentes} onChange={(e) => setSomentePendentes(e.target.checked)} />
          Só os que ainda não foram enviados{pendentes > 0 ? ` (${pendentes})` : ""}
        </label>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "var(--texto-suave)" }}>{itens.length} orçamento(s)</span>
          {podeEditar && (
            <button className="botao" onClick={() => setEditando(null)}>+ Novo Orçamento</button>
          )}
        </div>
      </div>

      {erro && <div className="erro" style={{ marginBottom: 12 }}>{erro}</div>}

      <div className="cartao" style={{ padding: 0, overflowX: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>Responsável</th>
              <th>Aluno</th>
              <th>Telefone</th>
              <th>Curso</th>
              <th>Série</th>
              <th>Valor c/ Desc.</th>
              <th>Enviado</th>
              <th style={{ width: 180 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((v) => (
              <tr key={v.id}>
                <td><strong>{v.nomeResponsavel}</strong></td>
                <td>{v.nomeAluno}</td>
                <td style={{ whiteSpace: "nowrap" }}>{telefoneExibicao(v.telefoneResponsavel, v.paisTelefone)}</td>
                <td style={{ color: "var(--texto-suave)" }}>{cursoCurto(v.curso?.nome)}</td>
                <td style={{ color: "var(--texto-suave)" }}>{v.serie?.nome ?? "—"}</td>
                <td style={{ fontSize: 11, whiteSpace: "nowrap" }}>{valorResumo(v)}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {v.orcamentoEnviadoEm ? (
                    <span style={{ fontSize: 11, color: VERDE, fontWeight: 600 }}>
                      ✓ {dataHoraCurta(v.orcamentoEnviadoEm)}
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, color: "#b54708", fontWeight: 600 }}>pendente</span>
                  )}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {podeEditar && (
                    <button onClick={() => setEditando(v)} title="Editar orçamento"
                      style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 4 }}>✏️</button>
                  )}
                  <button onClick={() => setImprimindo(v)} title="Gerar o PDF da proposta"
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 4 }}>📄</button>
                  {podeEditar && (
                    <>
                      <button onClick={() => abrirWhatsapp(v)} title="Abrir a conversa no WhatsApp para anexar o PDF"
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, padding: 4 }}>💬</button>
                      <button onClick={() => { setPromovendo(v); setQuando(""); }} title="A família quer visitar: transformar em visita agendada"
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, padding: 4 }}>📅</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {!carregando && visiveis.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", color: "var(--texto-suave)", padding: 32 }}>
                  {somentePendentes ? "Nenhum orçamento pendente de envio." : "Nenhum orçamento por aqui ainda."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {aviso && (
        <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 1000, maxWidth: 420, background: "#fffaeb", border: "1px solid #fec84b", color: "#b54708", padding: "12px 14px", borderRadius: 10, fontSize: 13, display: "flex", gap: 12, boxShadow: "0 8px 24px rgba(0,0,0,.12)" }}>
          <span style={{ flex: 1 }}>{aviso}</span>
          <button onClick={() => setAviso("")} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit" }}>✕</button>
        </div>
      )}

      {editando !== undefined && (
        <FormularioVisita
          visita={editando}
          apoio={apoio}
          orcamento
          onSalvar={salvar}
          onCancelar={() => setEditando(undefined)}
        />
      )}

      {imprimindo && (
        <FichaImpressao
          visita={imprimindo}
          catalogo={apoio.diferenciais}
          onFechar={() => setImprimindo(null)}
        />
      )}

      {confirmando && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 980, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 440, padding: "22px 24px", boxShadow: "0 24px 80px rgba(0,0,0,.25)" }}>
            <h3 style={{ margin: "0 0 10px", fontSize: 17, color: VERDE }}>Conseguiu enviar o PDF?</h3>
            <p style={{ fontSize: 14, color: "#344054", lineHeight: 1.6, margin: "0 0 18px" }}>
              A conversa com <strong>{confirmando.nomeResponsavel}</strong> abriu em outra aba com a
              mensagem pronta. Anexe o PDF e envie. Confirmando aqui, fica registrado quem enviou e quando.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="botao secundario" onClick={() => setConfirmando(null)} disabled={ocupado}>
                Ainda não
              </button>
              <button className="botao" onClick={() => marcarEnviado(confirmando)} disabled={ocupado}>
                {ocupado ? "Registrando..." : "Sim, enviei"}
              </button>
            </div>
          </div>
        </div>
      )}

      {promovendo && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 980, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 460, padding: "22px 24px", boxShadow: "0 24px 80px rgba(0,0,0,.25)" }}>
            <h3 style={{ margin: "0 0 10px", fontSize: 17, color: VERDE }}>Agendar a visita</h3>
            <p style={{ fontSize: 14, color: "#344054", lineHeight: 1.6, margin: "0 0 14px" }}>
              <strong>{promovendo.nomeAluno}</strong> — {promovendo.nomeResponsavel}
              <br />
              A proposta já preenchida vai junto: o orçamento vira visita, nada é redigitado.
            </p>
            <label className="rotulo">Data e hora da visita</label>
            <input type="datetime-local" className="campo" value={quando} onChange={(e) => setQuando(e.target.value)} />
            <p style={{ fontSize: 12.5, color: "#b54708", background: "#fffaeb", border: "1px solid #fedf89", borderRadius: 8, padding: "10px 12px", lineHeight: 1.6, margin: "14px 0 18px" }}>
              Ao confirmar, a família recebe automaticamente o WhatsApp de confirmação da visita.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="botao secundario" onClick={() => setPromovendo(null)} disabled={ocupado}>
                Cancelar
              </button>
              <button className="botao" onClick={confirmarPromocao} disabled={ocupado || !quando}>
                {ocupado ? "Agendando..." : "Agendar visita"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
